import { toArray } from './answerPath';

const OPTION_FIELD_TYPES = new Set(['dropdown', 'radio', 'checkbox']);

/**
 * The form builder only exposes `label` to users. The synthetic `value` field is
 * auto-generated and never shown in the builder UI. The public respondent renderer
 * stores/compares the label (with de-duplication suffixes) — not the schema value key.
 * Shared here so agent/API answers keyed by option.value can be normalized on hydrate.
 */
export function buildDropdownOptions(rawOptions) {
  const opts = Array.isArray(rawOptions) ? rawOptions : [];
  const seen = new Set();
  return opts.map((o, i) => {
    const rawLabel = o?.label;
    const rawValue = o?.value;
    const hasLabel = rawLabel != null && String(rawLabel) !== '';
    const base = hasLabel
      ? String(rawLabel)
      : (rawValue != null && String(rawValue) !== '' ? String(rawValue) : `__opt_${i}`);
    let v = base;
    let n = 2;
    while (seen.has(v)) { v = `${base} (${n++})`; }
    seen.add(v);
    return { ...o, value: v, label: hasLabel ? String(rawLabel) : v };
  });
}

function resolveRespondentOptionValue(rawOptions, storedValue) {
  if (storedValue == null || storedValue === '') return storedValue;
  const storedStr = String(storedValue);
  const rawOpts = Array.isArray(rawOptions) ? rawOptions : [];
  const normalized = buildDropdownOptions(rawOpts);

  if (normalized.some((opt) => opt.value === storedStr)) {
    return storedStr;
  }

  const rawIdx = rawOpts.findIndex((opt) => String(opt?.value ?? '') === storedStr);
  if (rawIdx >= 0) {
    return normalized[rawIdx]?.value ?? storedStr;
  }

  const labelIdx = rawOpts.findIndex((opt) => String(opt?.label ?? '') === storedStr);
  if (labelIdx >= 0) {
    return normalized[labelIdx]?.value ?? storedStr;
  }

  return storedStr;
}

function normalizeFieldAnswer(field, value) {
  const fieldType = String(field?.type || '');
  if (!OPTION_FIELD_TYPES.has(fieldType)) {
    return value;
  }
  if (fieldType === 'checkbox') {
    return toArray(value).map((item) => resolveRespondentOptionValue(field.options, item));
  }
  return resolveRespondentOptionValue(field.options, value);
}

function normalizeRowAnswers(children, row) {
  if (!Array.isArray(children) || !row || typeof row !== 'object') return row;
  const next = { ...row };
  let changed = false;
  for (const child of children) {
    const childKey = child?.key;
    if (!childKey || !Object.prototype.hasOwnProperty.call(next, childKey)) continue;
    const normalized = normalizeFieldAnswer(child, next[childKey]);
    if (normalized !== next[childKey]) {
      next[childKey] = normalized;
      changed = true;
    }
  }
  return changed ? next : row;
}

/**
 * Map server/agent answers onto the label-based option values the public renderer uses.
 * Idempotent for answers already stored in respondent format.
 */
export function normalizeRespondentAnswers(fields, answers = {}) {
  if (!Array.isArray(fields) || !answers || typeof answers !== 'object') {
    return answers && typeof answers === 'object' ? { ...answers } : {};
  }

  const next = { ...answers };
  let changed = false;

  for (const field of fields) {
    const key = field?.key;
    if (!key || !Object.prototype.hasOwnProperty.call(next, key)) continue;

    const fieldType = String(field?.type || '');
    if (fieldType === 'repeatable_section') {
      const rows = next[key];
      if (!Array.isArray(rows)) continue;
      const children = field.children;
      const normalizedRows = rows.map((row) => normalizeRowAnswers(children, row));
      if (normalizedRows.some((row, idx) => row !== rows[idx])) {
        next[key] = normalizedRows;
        changed = true;
      }
      continue;
    }

    const normalized = normalizeFieldAnswer(field, next[key]);
    if (normalized !== next[key]) {
      next[key] = normalized;
      changed = true;
    }
  }

  return changed ? next : answers;
}
